import React, { useEffect, useState } from 'react';
import { devError } from '../lib/devLog';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FeaturedPhotographer from '../components/FeaturedPhotographer';
import MagnaniVolIIITrailer from '../components/MagnaniVolIIITrailer';
import HeroCinematic from '../components/HeroCinematic';
import PriceTag from '../components/PriceTag';
import OptimisedImage from '../components/OptimisedImage';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Phone, Mail, MapPin } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.3], [1, 0.95]);
  const [featuredArtworks, setFeaturedArtworks] = useState([]);
  const [latestArtworks, setLatestArtworks] = useState([]);
  const [mediumTiles, setMediumTiles] = useState([]);
  const [heroIndex, setHeroIndex] = useState(0);
  const [stats, setStats] = useState({ artworks: 0, artists: 0, years: 0 });

  useEffect(() => {
    // Live stats for hero
    fetch(`${API_URL}/api/stats`)
      .then(res => res.json())
      .then(setStats)
      .catch(err => devError('Error fetching stats:', err));

    // Hero rotates through AVAILABLE works spanning different artists (one per artist)
    fetch(`${API_URL}/api/artworks?available=true&limit=200`)
      .then(res => res.json())
      .then(data => {
        const seenArtists = new Set();
        const diverse = [];
        for (const w of data || []) {
          const name = w.artistName || 'Unknown';
          if (seenArtists.has(name)) continue;
          seenArtists.add(name);
          if (!w.imageUrl) continue;
          diverse.push(w);
          if (diverse.length >= 6) break;
        }
        setFeaturedArtworks(diverse);
      })
      .catch(err => devError('Error fetching featured artworks:', err));

    // Fetch latest artworks — "our newest additions" shelf must show
    // every genuinely-recent upload in chronological order, without any
    // one artist crowding the reel out. We fetch a wider window and show
    // newest-first, capped only at the shelf size.
    (async () => {
      try {
        const [newestRes, featuredRes] = await Promise.all([
          fetch(`${API_URL}/api/artworks?available=true&limit=40`).then((r) => r.json()),
          fetch(`${API_URL}/api/artworks?featured=true&available=true&limit=4`).then((r) => r.json()),
        ]);
        const newest = Array.isArray(newestRes) ? newestRes : [];
        const pinned = Array.isArray(featuredRes) ? featuredRes : [];

        const SHELF_SIZE = 16;  // 4 rows on desktop (grid-cols-4)

        const combined = newest.slice(0, SHELF_SIZE);
        const seen = new Set(combined.map((a) => a.id));
        // Top up remaining slots with admin-pinned favourites (only used
        // on a fresh install with fewer than SHELF_SIZE imports).
        for (const w of pinned) {
          if (combined.length >= SHELF_SIZE) break;
          if (seen.has(w.id)) continue;
          combined.push(w);
          seen.add(w.id);
        }
        setLatestArtworks(combined);
      } catch (err) {
        devError('Error fetching latest artworks:', err);
      }
    })();

    // Browse-by-Medium tiles: one cover image per medium + accurate count
    const MEDIUMS = ['Painting', 'Photography', 'Sculpture', 'Prints', 'Drawings'];
    Promise.all(
      MEDIUMS.map(async (medium) => {
        try {
          const res = await fetch(`${API_URL}/api/artworks?category=${encodeURIComponent(medium)}&limit=1000`);
          const data = await res.json();
          const cover =
            (data || []).find((a) => a.imageUrl && a.available) ||
            (data || []).find((a) => a.imageUrl);
          return { medium, count: (data || []).length, imageUrl: cover ? cover.imageUrl : null };
        } catch {
          return { medium, count: 0, imageUrl: null };
        }
      })
    ).then((tiles) => {
      // Drop empty mediums (e.g. if Drawings has 0 artworks)
      setMediumTiles(tiles.filter((t) => t.count > 0 && t.imageUrl));
    });
    // Mount-only. API_URL + MEDIUMS are module-level constants.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-rotate hero every 6s
  useEffect(() => {
    if (featuredArtworks.length < 2) return;
    const id = setInterval(() => {
      setHeroIndex((i) => (i + 1) % featuredArtworks.length);
    }, 6000);
    return () => clearInterval(id);
  }, [featuredArtworks.length]);

  return (
    <div className="bg-white min-h-screen">
      <Header />
      
      {/* Hero Section - Cinematic Mayfair scenes */}
      <HeroCinematic stats={stats} />

      {/* Featured Photographer rotation — 4-tile editorial spotlight */}
      <FeaturedPhotographer />

      {/* Sebastian Magnani — Daily Bat Volume III (2025) trailer */}
      <MagnaniVolIIITrailer />

      {/* Services Section */}
      <section className="max-w-[1400px] mx-auto px-6 py-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-8 border border-gray-200 rounded-xl hover:shadow-lg transition-shadow"
          >
            <h3 className="text-2xl font-light mb-4">Art Gallery</h3>
            <p className="text-gray-600 mb-6">
              Discover our curated collection of contemporary art, photography, and post-war modern pieces from emerging and established artists.
            </p>
            <a href="/shop" className="text-sm font-medium hover:underline">
              View Collection →
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="p-8 border border-gray-200 rounded-xl hover:shadow-lg transition-shadow"
          >
            <h3 className="text-2xl font-light mb-4">Art Consultation</h3>
            <p className="text-gray-600 mb-6">
              Full-service art consultancy from collection audits to procurement, installation, and ongoing curation for your space.
            </p>
            <a href="/art-consultation" className="text-sm font-medium hover:underline">
              Learn More →
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="p-8 border border-gray-200 rounded-xl hover:shadow-lg transition-shadow"
          >
            <h3 className="text-2xl font-light mb-4">Picture Framing</h3>
            <p className="text-gray-600 mb-6">
              Bespoke picture framing service with expert advice on conservation and presentation of your artworks.
            </p>
            <a href="/picture-framing" className="text-sm font-medium hover:underline">
              Framing Services →
            </a>
          </motion.div>
        </div>
      </section>

      {/* Browse by Medium */}
      {mediumTiles.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-6 py-24" data-testid="browse-by-medium">
          <div className="mb-10 flex items-end justify-between gap-4 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-neutral-500 mb-3">Browse the collection</p>
              <h2 className="text-4xl md:text-5xl font-light leading-tight">By Medium</h2>
            </div>
            <Link to="/shop" className="text-sm underline-offset-4 hover:underline text-neutral-600">
              View all works →
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {mediumTiles.map((tile, idx) => (
              <Link
                key={tile.medium}
                to={`/shop?category=${encodeURIComponent(tile.medium)}`}
                data-testid={`medium-tile-${tile.medium.toLowerCase()}`}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05, duration: 0.5 }}
                  whileHover={{ y: -6 }}
                  className="group relative aspect-[3/4] overflow-hidden bg-neutral-100"
                >
                  <img
                    src={tile.imageUrl.startsWith('http') ? tile.imageUrl : `${API_URL}${tile.imageUrl}`}
                    alt={tile.medium}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                    <h3 className="text-2xl md:text-3xl font-light leading-none">{tile.medium}</h3>
                    <p className="mt-2 text-xs tracking-widest uppercase text-white/70">
                      {tile.count} {tile.count === 1 ? 'work' : 'works'}
                    </p>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Latest Artworks Section */}
      <section className="bg-gray-50 py-24">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-light mb-4">Latest Artworks Available</h2>
            <p className="text-xl text-gray-600">Explore our newest additions</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {latestArtworks.map((artwork, index) => (
              <Link to={`/artwork/${artwork.slug || artwork.id}`} key={artwork.id} data-testid={`home-latest-artwork-${artwork.id}`}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ y: -10 }}
                  className="group cursor-pointer"
                >
                  <div className="bg-neutral-50 aspect-square rounded-lg mb-3 overflow-hidden flex items-center justify-center">
                    {artwork.imageUrl ? (
                      <OptimisedImage
                        src={artwork.imageUrl}
                        alt={artwork.title}
                        width={480}
                        loading="lazy"
                        className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-gray-300 to-gray-200" />
                    )}
                  </div>
                  <p className="text-sm font-medium group-hover:underline truncate">{artwork.artistName}</p>
                  <p className="text-xs text-gray-600 truncate">{artwork.title}</p>
                  <PriceTag
                    priceDisplay={artwork.priceDisplay}
                    vatScheme={artwork.vatScheme || 'standard'}
                    className="text-xs text-gray-500 mt-1"
                  />
                </motion.div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <motion.a
              href="/shop"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-block px-6 py-3 border border-black rounded-full hover:bg-black hover:text-white transition-colors"
            >
              View All Artworks
            </motion.a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="max-w-[1400px] mx-auto px-6 py-24">
        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <h2 className="text-4xl font-light mb-6">Visit Us</h2>
            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 mt-1" />
                <div>
                  <p className="font-medium">Zebra One Gallery</p>
                  <p className="text-gray-600">73a Hampstead High Street</p>
                  <p className="text-gray-600">Hampstead, London NW3 1QX</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5" />
                <a href="tel:02077941281" className="hover:underline">020 7794 1281</a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5" />
                <a href="mailto:info@zebraonegallery.com" className="hover:underline">
                  info@zebraonegallery.com
                </a>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-4xl font-light mb-6">Get In Touch</h2>
            <form className="space-y-4">
              <input
                type="text"
                placeholder="Full Name"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
              <input
                type="email"
                placeholder="Email Address"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
              <textarea
                rows={4}
                placeholder="Message"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                Send Message
              </motion.button>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
